import { Input } from '../../../shared/components/FormControls';
import Rodal from 'rodal';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from '../../../shared/components/Button';
import { useNavigate } from 'react-router-dom';
import 'rodal/lib/rodal.css';
import '../../../shared/styles/Toastify.css';

import trialmentLogo from '../../../shared/assets/logos/trialmentlogo.png';
import daggerheartLogo from '../../../shared/assets/logos/daggerheart.png';
import cotaLogo from '../../../shared/assets/logos/cotalogo.png';
import cotaTitle from '../../../shared/assets/logos/cotatitle.png';
import orchestratorTitle from '../../../shared/assets/logos/orchestratortitle.png';
import orchestratorPlayerTitle from '../../../shared/assets/logos/orchestratorplayertitle.png';
import orchestratorLogo from '../../../shared/assets/logos/orchestratorlogo.png';
import orchestratorLoginBackground from '../../../shared/assets/loginanimations/orchestratorlogin.gif';
import orchestratorPlayerLoginBackground from '../../../shared/assets/loginanimations/orchestratorplayerlogin.gif';
import cotaLoginBackground from '../../../shared/assets/loginanimations/cotalogin.gif';
import trialmentLoginBackground from '../../../shared/assets/loginanimations/trialmentlogin.gif';

import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
} from 'firebase/auth';
import { auth, db, googleProvider } from '../../../firebase/firebaseApp';
import {
  fetchProvisionedUser,
  hasModuleAccess,
} from '../utils/provisioning';
import { matchLegacyCotaAccount } from '../utils/legacyAccounts';
import { ArrowRight, Eye, EyeOff, LogIn } from 'lucide-react';

const VALID_SYSTEMS = new Set(['cota', 'orchestrator', 'orchestrator_user']);

export const LoginModal = (props) => {
  const { system, show, close, onRollDuality } = props;

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [backgroundSize, setBackgroundSize] = useState(null);
  const [backgroundPosition, setBackgroundPosition] = useState(null);
  const [backgroundRepeat, setBackgroundRepeat] = useState(null);
  const [isPhone, setIsPhone] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const mediaQueryWidth = window.matchMedia('(max-width: 500px)');
    const handleMediaChange = () => setIsPhone(mediaQueryWidth.matches);
    handleMediaChange();
    mediaQueryWidth.addListener(handleMediaChange);
    return () => mediaQueryWidth.removeListener(handleMediaChange);
  }, []);

  const normalizedEmail = useMemo(() => username.trim(), [username]);
  const normalizedPassword = useMemo(() => password.trim(), [password]);

  const isEnterEnabled = useMemo(() => {
    if (loading || !show) return false;
    if (!VALID_SYSTEMS.has(system)) return false;
    return normalizedEmail !== '' && normalizedPassword !== '';
  }, [
    loading,
    show,
    system,
    normalizedEmail,
    normalizedPassword,
  ]);

  const isGoogleEnabled = useMemo(() => {
    if (loading || !show) return false;
    if (!VALID_SYSTEMS.has(system)) return false;
    return true;
  }, [loading, show, system]);

  const getIconClass = (system) => {
    if (system === 'cota') return 'cota_login_logo';
    if (system === 'orchestrator' || system === 'orchestrator_user') {
      return 'trialment_login_logo';
    }
    return '';
  };

  const toggleShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const guardAuthAttempt = useCallback(() => {
    if (!VALID_SYSTEMS.has(system)) {
      toast.error('Unsupported module selected.');
      return false;
    }

    return true;
  }, [system]);

  const resolveProvisionedEmail = useCallback(
    async (candidateEmail) => {
      try {
        const record = await fetchProvisionedUser(db, candidateEmail);
        if (!record) {
          await signOut(auth).catch(() => { });
          toast.info(
            'Thanks for showing interest in Dimension Factory. The modules of Dimension Factory is in beta testing.'
          );
          return null;
        }

        if (!hasModuleAccess(record.data, system)) {
          await signOut(auth).catch(() => { });
          toast.error(
            'You do not have access to this module. Please contact your administrator.'
          );
          return null;
        }

        return { email: record.email, name: record.data?.name ?? '' };
      } catch (error) {
        console.error('Provisioning verification failed', error);
        await signOut(auth).catch(() => { });
        toast.error(
          'We could not verify your account provisioning. Please try again later or reach out to your admin.'
        );
        return null;
      }
    },
    [system]
  );

  const getReadableFirebaseError = (error) => {
    if (!error || !error.code) {
      return 'Unable to sign in. Please try again.';
    }

    switch (error.code) {
      case 'auth/user-not-found':
        return 'No account found for the provided email.';
      case 'auth/wrong-password':
      case 'auth/invalid-credential':
        return `The email, password, or account provider are incorrect, If you've done so in the past, try logging in with Google`;
      case 'auth/too-many-requests':
        return 'Too many attempts. Please try again later.';
      default:
        return 'Failed to authenticate. Please try again.';
    }
  };

  const finalizeLogin = useCallback(
    (email, name = '') => {
      if (!email) {
        return;
      }
      localStorage.setItem('username', email);
      localStorage.setItem('displayName', name || email);
      localStorage.setItem('system', system);
      localStorage.removeItem('campaign');

      if (typeof close === 'function') {
        close();
      }

      navigate(
        system === 'orchestrator'
          ? '/orchestrator'
          : system === 'orchestrator_user'
            ? '/orchestrator/player'
            : '/campaign'
      );
    },
    [close, navigate, system]
  );

  const handleEmailLogin = useCallback(async () => {
    if (!isEnterEnabled || !guardAuthAttempt()) {
      return;
    }

    if (system === 'cota') {
      const legacyAccount = matchLegacyCotaAccount(
        normalizedEmail,
        normalizedPassword
      );

      if (legacyAccount) {
        finalizeLogin(legacyAccount.username);
        return;
      }
    }

    try {
      setLoading(true);

      const credential = await signInWithEmailAndPassword(
        auth,
        normalizedEmail,
        normalizedPassword
      );

      const provisioned = await resolveProvisionedEmail(
        credential.user?.email ?? normalizedEmail
      );

      if (!provisioned) {
        return;
      }

      finalizeLogin(provisioned.email, provisioned.name);
    } catch (error) {
      const message = getReadableFirebaseError(error);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [
    guardAuthAttempt,
    isEnterEnabled,
    resolveProvisionedEmail,
    finalizeLogin,
    normalizedEmail,
    normalizedPassword,
    system,
  ]);

  const handleGoogleLogin = useCallback(async () => {
    if (!isGoogleEnabled || !guardAuthAttempt()) {
      return;
    }

    try {
      setLoading(true);

      const result = await signInWithPopup(auth, googleProvider);
      const provisioned = await resolveProvisionedEmail(result.user?.email);

      if (!provisioned) {
        return;
      }

      finalizeLogin(provisioned.email, provisioned.name);
    } catch (error) {
      if (
        error?.code === 'auth/popup-closed-by-user' ||
        error?.code === 'auth/cancelled-popup-request'
      ) {
        return;
      }

      const message =
        error?.code === 'auth/account-exists-with-different-credential'
          ? 'This email is linked to another sign-in method. Use the associated provider.'
          : 'Google sign-in failed. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [guardAuthAttempt, isGoogleEnabled, resolveProvisionedEmail, finalizeLogin]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Enter' && isEnterEnabled) {
        e.preventDefault();
        handleEmailLogin();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleEmailLogin, isEnterEnabled]);

  useEffect(() => {
    if (
      system === 'orchestrator' ||
      system === 'orchestrator_user' ||
      system === 'cota'
    ) {
      setBackgroundSize('cover, cover');
      setBackgroundPosition('center, center');
      setBackgroundRepeat('no-repeat, no-repeat');
    }
    if (!show) {
      setTimeout(() => {
        setUsername('');
        setPassword('');
        setShowPassword(false);
        setLoading(false);
        setBackgroundSize(null);
        setBackgroundPosition(null);
        setBackgroundRepeat(null);
      }, 300);
    }
  }, [system, show]);

  return (
    <Rodal
      animation={'fade'}
      visible={show}
      onClose={!loading ? close : () => { }}
      //showMask={false}
      customStyles={{
        border: '2px solid #e4E7C5', //beige
        backgroundColor: '#f6f7eb', //ivory
        // Layered backgrounds: ivory gradient on top of image to simulate ~30% image opacity
        background:
          system === 'trialment'
            ? `linear-gradient(rgba(246,247,235,0.70), rgba(246,247,235,0.70)), url(${trialmentLoginBackground})`
            : system === 'orchestrator' ||
              system === 'cota' ||
              system === 'orchestrator_user'
              ? `linear-gradient(rgba(246,247,235,${system === 'cota' ? '0.55' : '0.7'
              }), rgba(246,247,235,${system === 'cota' ? '0.55' : '0.7'
              })), url(${system === 'orchestrator'
                ? orchestratorLoginBackground
                : system === 'cota'
                  ? cotaLoginBackground
                  : orchestratorPlayerLoginBackground
              })`
              : '#f6f7eb',
        backgroundSize: system === 'trialment' ? 'cover, cover' : backgroundSize,
        backgroundPosition: system === 'trialment' ? 'center, center' : backgroundPosition,
        backgroundRepeat: system === 'trialment' ? 'no-repeat, no-repeat' : backgroundRepeat,
        ...(system === 'trialment' && {
          border: '2px solid #e4E7C5',
          color: 'var(--jet)',
          padding: '18px 10px 12px 20px',
          width: isPhone ? 'calc(100% - 34px)' : '400px',
          height: isPhone ? 'calc(100% - 34px)' : '567px',
        }),
      }}
      height={
        system === 'trialment'
          ? isPhone ? 'calc(100% - 34px)' : 567
          : system === 'orchestrator_user'
            ? 480
            : 475
      }
      width={system === 'trialment' ? isPhone ? 'calc(100% - 34px)' : 400 : 360}
    >
      <ToastContainer
        position='bottom-center'
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme='colored'
      />
      {system !== 'trialment' && <div>
        <img
          className={`system_logo ${getIconClass(system)}`}
          src={
            system === 'trialment'
              ? trialmentLogo
              : system === 'cota'
                ? cotaLogo
                : system === 'orchestrator' || system === 'orchestrator_user'
                  ? orchestratorLogo
                  : ''
          }
          alt=''
        />
      </div>}
      {system !== 'trialment' && <div>
        <img
          className={`${system === 'orchestrator_user' ? 'orchestrator_user_logo_txt' : 'system_logo_txt'} ${system === 'orchestrator'
            ? 'trialment_login_logo'
            : //? 'orchestrator_login_logo'
            'trialment_login_logo'
            }`}
          src={
            system === 'cota'
              ? cotaTitle
              : system === 'orchestrator'
                ? orchestratorTitle
                : system === 'orchestrator_user'
                  ? orchestratorPlayerTitle
                  : ''
          }
          alt=''
        />
      </div>}
      {
        system === 'trialment' ? (
          <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div className='trialment_sunset_message'>
              <div className='trialment_sunset_logos' aria-label='Trialment transitions to Daggerheart'>
                <img src={trialmentLogo} alt='Trialment' />
                <ArrowRight aria-hidden='true' size={28} />
                <img src={daggerheartLogo} alt='Daggerheart' />
              </div>
              <h2>Thanks for playing Trialment!</h2>
              <h3>A note from the creator:</h3>
              <p>
                It's been a privilege working on the Trialment TTRPG system since 2017, however, ever since Daggerheart released, I was drawn to it immediately. It became clear to me that the two systems are not only extremely similar in terms of play, but philosophically aligned.
              </p>
              <p>
                If Trialment were ever to release, it would be too similar to an existing, popular system, one that I personally am really fond of. And so, as of November the 1st 2026, Trialment’s future is within Daggerheart itself.
              </p>
              <p>
                What does this mean for you? Well, the Hope™ is to release the Trialment Campaigns (Coves of Elvion, The Cradle, Mother Sea, and the upcoming The Linchpin) as Daggerheart Campaign Frames. Stay tuned for more updates in 2027!
              </p>
              <p className='trialment_sunset_signature'>-Théodred</p>
            </div>
            <div style={{ marginTop: 12, display: 'flex', justifyContent: 'center' }}>
              <Button label='Roll Duality!' type='starfinder2' description='Yellow is Hope, purple is Fear' descriptionUp onPress={() => {
                onRollDuality?.();
                close();
              }} />
            </div>
          </div>
        ) : (
          <>
            <form className='trialment_login_form'>
              <div className='trialment_login_form_row'>Login</div>
              <div className='trialment_login_form_row trialment_login_form_input_row'>
                <Input
                  type='text'
                  value={username}
                  disabled={loading}
                  placeholder='Email'
                  onChange={(e) => {
                    if (!loading) {
                      setUsername(e.target.value);
                    }
                  }}
                  autoComplete='email'
                />
              </div>
              <div
                className='trialment_login_form_row trialment_login_form_input_row'
                style={{ position: 'relative' }}
              >
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  disabled={loading}
                  placeholder='Password'
                  onChange={(e) => {
                    if (!loading) {
                      setPassword(e.target.value);
                    }
                  }}
                  autoComplete='current-password'
                  style={{ paddingRight: '36px' }}
                />
                <button
                  type='button'
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={showPassword}
                  onClick={toggleShowPassword}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '60%',
                    transform: 'translateY(-50%)',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: 4,
                    border: 0,
                    background: 'transparent',
                    color: 'black',
                  }}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </form>
            <div style={{ paddingTop: '10px', marginBottom: '10px' }}>
              <Button
                label={'Login with Google'}
                type={
                  loading
                    ? 'placeholder'
                    : isGoogleEnabled
                      ? 'blue'
                      : 'disabled'
                }
                disabled={!isGoogleEnabled || loading}
                loading={loading}
                onPress={handleGoogleLogin}
              />
              <span className='button_margin'>
                <Button label={'Forgot Password'} type={'disabled'} />
              </span>
            </div>

            <div style={{ paddingTop: '5px', marginBottom: '10px' }}>
              <Button
                icon={LogIn}
                label={'Enter'}
                type={
                  loading ? 'placeholder' : isEnterEnabled ? 'cinnabar' : 'disabled'
                }
                disabled={!isEnterEnabled || loading}
                loading={loading}
                onPress={handleEmailLogin}
              />
            </div>
          </>
        )
      }
    </Rodal >
  );
};
